import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncDataState<T> {
  data: T | null;
  /** True only on the first load, so the UI can show skeletons once. */
  loading: boolean;
  /** True while re-fetching with data already on screen. */
  refreshing: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches on mount and whenever `deps` change, cancelling the previous request.
 *
 * Distinguishing `loading` from `refreshing` matters on paged tables: page two
 * should dim, not blank out and re-skeleton.
 */
export function useAsyncData<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncDataState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const hasLoaded = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();

    if (hasLoaded.current) setRefreshing(true);
    else setLoading(true);

    fetcherRef
      .current(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setError(null);
        hasLoaded.current = true;
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        // A cancelled request is not a failure the user needs to hear about.
        if (caught instanceof Error && caught.name === "CanceledError") return;
        setError(
          caught instanceof Error ? caught.message : "Something went wrong.",
        );
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { data, loading, refreshing, error, reload };
}
